import { featureFlag, useFeatureFlag } from '@routable/feature-configs';
import { useEvent } from '@routable/framework';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ITEM_STATUS_CHANGING_TO_ANALYZING } from 'constants/events';
import { useAnnotations } from 'helpers/ocr';
import { useOcrDataFromOverlays, useSelectorWithProps } from 'hooks';
import { useRoutableParams } from 'hooks/useRoutableParams';
import { createItemFormShouldShowPoSectionSelector } from 'queries/createItemFormSelectors';
import { createItemFormItemSelector, createItemFormUiIsItemEditSelector, createItemFormUiIsPurchaseOrderFetchedSelector, createItemFormUiSelectedCompanySelector, createItemFormUiSkipPurchaseOrderSelector, } from 'selectors/forms';
import { isFetchingItemSelector } from 'selectors/itemsSelectors';
import { isFetchingPartnershipSelector } from 'selectors/partnershipsSelectors';
export const CreateItemFormContext = createContext(undefined);
export const CreateItemFormProvider = ({ children }) => {
    const isConsolidatedLoaderEnabled = useFeatureFlag(featureFlag.Enum.item_form_consolidated_loader);
    const [containerWidths, setContainerWidths] = useState({ leftSize: undefined, rightSize: undefined });
    const item = useSelector(createItemFormItemSelector);
    const isFetchingPartnership = useSelector(isFetchingPartnershipSelector);
    const isItemEdit = useSelector(createItemFormUiIsItemEditSelector);
    const showPoSection = useSelectorWithProps(createItemFormShouldShowPoSectionSelector, isConsolidatedLoaderEnabled);
    const isPoSkipped = useSelector(createItemFormUiSkipPurchaseOrderSelector);
    const selectedCompany = useSelector(createItemFormUiSelectedCompanySelector);
    const isFetchingItem = useSelector(isFetchingItemSelector);
    const isPoFetched = useSelector(createItemFormUiIsPurchaseOrderFetchedSelector);
    const location = useLocation();
    const { id: itemId } = useRoutableParams();
    const { annotationData } = useAnnotations(itemId);
    const { isLoading: isLoadingOcr } = useOcrDataFromOverlays({ itemId: !isItemEdit ? itemId : undefined });
    const isMissingPredictedPayeeName = annotationData && !annotationData.item?.predictedPayeeName;
    const [isFetchingLedgerOnlyPartnership, setIsFetchingLedgerOnlyPartnership] = useState(false);
    const [finishedFetchingPartnership, setFinishedFetchingPartnership] = useState(false);
    const isItemFetching = useSelector(isFetchingItemSelector);
    const [isSummarizeInitialized, setIsSummarizeInitialized] = useState(false);
    const [isSummarized, setIsSummarized] = useState(true);
    const shouldFetchPOs = Boolean(showPoSection && !isPoSkipped && selectedCompany?.vendorRef);
    const [isFormLoading, setIsFormLoading] = useState(true);
    const [isRestOfFormLoading, setIsRestOfFormLoading] = useState(true);
    const isItemStatusChangingToAnalyzing = useEvent(ITEM_STATUS_CHANGING_TO_ANALYZING, (e) => {
        return !!e.isAnalyzing;
    });
    const isLoadingItemOrPartnership = isItemStatusChangingToAnalyzing || isLoadingOcr || isItemFetching || isFetchingPartnership;
    const companyInLocationState = typeof location.state === 'object' && !!location.state && 'selectedCompany' in location.state
        ? location.state.selectedCompany
        : null;
    useEffect(() => {
        if (!finishedFetchingPartnership &&
            (companyInLocationState ? !!selectedCompany : !itemId || selectedCompany || isMissingPredictedPayeeName)) {
            setFinishedFetchingPartnership(true);
        }
    }, [finishedFetchingPartnership, isMissingPredictedPayeeName, itemId, selectedCompany, companyInLocationState]);
    useEffect(() => {
        setIsRestOfFormLoading(isLoadingItemOrPartnership);
    }, [isLoadingItemOrPartnership]);
    useEffect(() => {
        if (isPoFetched) {
            setIsFormLoading(false);
        }
    }, [isPoFetched]);
    useEffect(() => {
        if (isFormLoading &&
            !!item &&
            finishedFetchingPartnership &&
            !isFetchingPartnership &&
            !isLoadingOcr &&
            !isFetchingItem &&
            !shouldFetchPOs) {
            setIsFormLoading(false);
        }
    }, [
        finishedFetchingPartnership,
        isFetchingItem,
        isFetchingPartnership,
        isFormLoading,
        isLoadingOcr,
        item,
        shouldFetchPOs,
    ]);
    return (React.createElement(CreateItemFormContext.Provider, { value: {
            containerWidths,
            isConsolidatedLoaderEnabled,
            isFetchingLedgerOnlyPartnership: !!isConsolidatedLoaderEnabled && isFetchingLedgerOnlyPartnership,
            isFormLoading: !!isConsolidatedLoaderEnabled && isFormLoading,
            isLoadingItemOrPartnership,
            isRestOfFormLoading: !!isConsolidatedLoaderEnabled && (isFormLoading || isRestOfFormLoading),
            isSummarized,
            isSummarizeInitialized,
            setContainerWidths,
            setIsFetchingLedgerOnlyPartnership,
            setIsFormLoading,
            setIsSummarized,
            setIsSummarizeInitialized,
            setIsRestOfFormLoading,
        } }, children));
};
export const useCreateItemFormContext = () => {
    const createItemFormContext = useContext(CreateItemFormContext);
    if (!createItemFormContext) {
        return {};
    }
    return createItemFormContext;
};
